import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PATH_TO_LANGUAGE_MAP } from '../config';
import { useLanguage, type Language } from '../lib/i18n';
import GamePage from '../routes/game';
import HomePage from '../routes/home';
import CategoryPage from '../routes/category';
import SearchPage from '../routes/search';

/**
 * Handles /:slug — slug can be either:
 * - A locale path segment (e.g. 'vn', 'jp', 'zh') → render HomePage with that language
 * - A game slug (e.g. 'pokerogue') → render GamePage as usual
 *
 * Both cases use the same route param name "slug", so GamePage.useParams().slug works.
 */
export const LocaleOrGameRoute: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { changeLanguage } = useLanguage();
  const langCode = slug ? PATH_TO_LANGUAGE_MAP[slug] : undefined;

  useEffect(() => {
    if (langCode) {
      changeLanguage(langCode as Language);
    }
  }, [langCode, changeLanguage]);

  if (langCode) return <HomePage />;
  return <GamePage />;
};

/**
 * Handles /:locale/:slug — validates the locale prefix, sets the language,
 * then renders GamePage (which reads the "slug" param directly).
 */
export const LocaleGameRoute: React.FC = () => {
  const { locale } = useParams<{ locale: string; slug: string }>();
  const { changeLanguage } = useLanguage();
  const langCode = locale ? PATH_TO_LANGUAGE_MAP[locale] : undefined;

  useEffect(() => {
    if (langCode) {
      changeLanguage(langCode as Language);
    }
  }, [langCode, changeLanguage]);

  if (!langCode) return <p className="muted">Page not found.</p>;
  return <GamePage />;
};

/**
 * Handles /:locale/category/:slug — validates locale, sets language,
 * renders CategoryPage (which reads the "slug" param directly).
 */
export const LocaleCategoryRoute: React.FC = () => {
  const { locale } = useParams<{ locale: string; slug: string }>();
  const { changeLanguage } = useLanguage();
  const langCode = locale ? PATH_TO_LANGUAGE_MAP[locale] : undefined;

  useEffect(() => {
    if (langCode) {
      changeLanguage(langCode as Language);
    }
  }, [langCode, changeLanguage]);

  if (!langCode) return <p className="muted">Page not found.</p>;
  return <CategoryPage />;
};

/**
 * Handles /:locale/search — validates locale, sets language,
 * renders SearchPage.
 */
export const LocaleSearchRoute: React.FC = () => {
  const { locale } = useParams<{ locale: string }>();
  const { changeLanguage } = useLanguage();
  const langCode = locale ? PATH_TO_LANGUAGE_MAP[locale] : undefined;

  useEffect(() => {
    if (langCode) {
      changeLanguage(langCode as Language);
    }
  }, [langCode, changeLanguage]);

  if (!langCode) return <p className="muted">Page not found.</p>;
  return <SearchPage />;
};
